/** What a push job carries. One job stack, one progress UI, two kinds. */
export type PushJobKind = "Exercise" | "Workout";

export type PushJobStatus =
  | "Queued"
  | "Running"
  | "CancelRequested"
  | "Completed"
  | "CompletedWithFailures"
  | "Canceled"
  | "Interrupted"
  | "Failed";

export type PushJobStage = "Preparing" | "Pushing" | "Finalizing";

export interface IPushJobIssue {
  locationId: string;
  locationName: string;
  organizationName: string;
  code: string;
  message: string;
}

export interface IPushJobStatus {
  id: string;
  kind: PushJobKind;
  sourceLocationId: string;
  parentJobId?: string;
  status: PushJobStatus;
  stage: PushJobStage;
  targetMode: "AllActiveLocations" | "SelectedLocations" | "Retry";
  /** How many exercises or workouts the job carries, depending on `kind`. */
  objectCount: number;
  totalLocations: number;
  processedLocations: number;
  successfulLocations: number;
  failedLocations: number;
  skippedLocations: number;
  remainingLocations: number;
  progressPercent: number;
  currentLocationName?: string;
  currentOrganizationName?: string;
  createdAt: string;
  startedAt?: string;
  updatedAt: string;
  finishedAt?: string;
  canCancel: boolean;
  canRetry: boolean;
  errorMessage?: string;
  failures: IPushJobIssue[];
  warnings: IPushJobIssue[];
}

export interface IPushExercisesToLocationsRequest {
  exerciseIds: string[];
  locationIds: string[];
  toAllLocations: boolean;
  /** Whether users of the destination locations get a push notification. */
  notifyUsers?: boolean;
}

export interface IPushWorkoutsToLocationsRequest {
  workoutIds: string[];
  sourceLocationId: string;
  destinationLocationIds: string[];
  toAllLocations: boolean;
  /** Whether users of the destination locations get a push notification. */
  notifyUsers?: boolean;
}

export type PushJobRequest =
  | IPushExercisesToLocationsRequest
  | IPushWorkoutsToLocationsRequest;

export const TERMINAL_PUSH_JOB_STATUSES: PushJobStatus[] = [
  "Completed",
  "CompletedWithFailures",
  "Canceled",
  "Interrupted",
  "Failed",
];

/** Lower-case singular noun for a kind, for user-facing copy. */
export function pushJobNoun(kind: PushJobKind): string {
  return kind === "Workout" ? "workout" : "exercise";
}
